import "./App.css";
import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import RegisterPage from "./pages/Register";
import LoginPage from "./pages/Login";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPassword";
import VerifyEmailPage from "./pages/VerifyEmail";
import LandingPage from "./pages/LandingPage";
import AboutUsPage from "./pages/AboutUs";
import ContactPage from "./pages/Contact";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import TermsOfServicePage from "./pages/TermsOfService";
import ProfilePage from "./pages/Profile";
import CreateMealPlanPage from "./pages/CreateMealPlan";
import CreateExercisePlanPage from "./pages/CreateExercisePlan";
import MealDashboardPage from "./pages/MealDashboard";
import ExerciseDashboardPage from "./pages/ExerciseDashboard";
import HomeDashboardPage from "./pages/HomeDashboard";
import ProgressDashboardPage from "./pages/ProgressDashboard";
import TrackProgressPage from "./pages/TrackProgress";
import NotFoundPage from "./pages/NotFound";
import Loader from "./components/global/loader";
import { Toaster } from "react-hot-toast";
import useStore from "services/store";
import api from "services/api";


const validateUser = async (user) => {
  if (!user) return false;

  try {
    const response = await api.get("/auth/validate-token");
    return response.ok;
  } catch (error) {
    return false;
  }
};


const PublicRoute = ({ children }) => {
  const { user } = useStore();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const validate = async () => {
      const valid = await validateUser(user);
      setValid(valid);
      setLoading(false);
    };
    validate();
  }, [user]);

  if (loading) {
    return <Loader />;
  }

  if (user && valid) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useStore();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const validate = async () => {
      const valid = await validateUser(user);
      setValid(valid);
      setLoading(false);
    };
    validate();
  }, [user]);

  if (loading) {
    return <Loader />;
  }

  if (!user || !valid) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
};


function App() {
  return (
    <Router>
      <Toaster position="top-center" />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
        <Route path="/verify-email" element={<PublicRoute><VerifyEmailPage /></PublicRoute>} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/home" element={<ProtectedRoute><HomeDashboardPage /></ProtectedRoute>} />
        <Route path="/meal-dashboard" element={<ProtectedRoute><MealDashboardPage /></ProtectedRoute>} />
        <Route path="/exercise-dashboard" element={<ProtectedRoute><ExerciseDashboardPage /></ProtectedRoute>} />
        <Route path="/create-meal-plan" element={<ProtectedRoute><CreateMealPlanPage /></ProtectedRoute>} />
        <Route path="/create-exercise-plan" element={<ProtectedRoute><CreateExercisePlanPage /></ProtectedRoute>} />
        <Route path="/progress-dashboard" element={<ProtectedRoute><ProgressDashboardPage /></ProtectedRoute>} />
        <Route path="/track-progress" element={<ProtectedRoute><TrackProgressPage /></ProtectedRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ScrollToTop />
    </Router>
  );
}

export default App;
